import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { AuthService, ErrorHandlerService } from '@my-sport-app/domain';
import { ToastService } from '../../toast/toast.service';
import { BoAuthBrandComponent } from '../bo-auth-brand/bo-auth-brand.component';
import { BoAuthCardComponent } from '../bo-auth-card/bo-auth-card.component';
import { BoAuthInputComponent } from '../bo-auth-input/bo-auth-input.component';
import { BoAuthSubmitButtonComponent } from '../bo-auth-submit-button/bo-auth-submit-button.component';

/**
 * Transversal "forgot password" view shared by the player and business-owner
 * applications. Both apps consume the same backend endpoint
 * (AuthService.resetPassword -> POST /auth/forgot-password) and the same
 * bo-auth design system, so a single component guarantees identical behavior
 * and visual consistency.
 *
 * The "back to login" target is resolved from the route `data.loginRoute`
 * (falls back to `/auth/login`), so each app can point back at its own login.
 */
@Component({
  selector: 'lib-bo-auth-forgot-password',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    BoAuthCardComponent,
    BoAuthBrandComponent,
    BoAuthInputComponent,
    BoAuthSubmitButtonComponent,
  ],
  templateUrl: './bo-auth-forgot-password.component.html',
  styleUrls: ['./bo-auth-forgot-password.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BoAuthForgotPasswordComponent {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly toastService = inject(ToastService);

  readonly form = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
  });
  readonly isLoading = signal(false);
  readonly emailSent = signal(false);

  get loginRoute(): string {
    return (this.route.snapshot.data['loginRoute'] as string | undefined) ?? '/auth/login';
  }

  onSubmit(): void {
    const emailControl = this.form.controls.email;
    if (emailControl.invalid || this.isLoading()) {
      return;
    }

    const value = (emailControl.value ?? '').trim();
    if (!value) {
      return;
    }

    this.isLoading.set(true);

    this.authService.resetPassword(value).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.emailSent.set(true);
      },
      error: (error: unknown) => {
        this.isLoading.set(false);
        this.toastService.showApplicationError(this.errorHandler.mapForgotPasswordError(error));
      },
    });
  }

  resend(): void {
    this.emailSent.set(false);
    this.form.reset();
  }
}
